/**
 * Loan App Demo — API Service Layer
 *
 * Wraps all real backend API calls for the demo flow:
 *   1. DoqSolv  — Fetch template schema + generate a filled PDF
 *   2. DocuStamp — Consume a stamp paper from a bulk order
 *   3. PDF Merge — Client-side merge of stamp paper + agreement
 *   4. eSign    — Send merged PDF to Meon for Aadhaar eSign
 *   5. Email    — Send completion email with generated document
 *
 * Authentication is via X-API-Key header. The demo route itself
 * sits inside <AuthProvider>, so the page can only be loaded
 * after a valid OMS session exists in the browser.
 */

import axios from 'axios';
import { PDFDocument } from 'pdf-lib';
import DEMO_CONFIG from './demoConfig';

// ─── Shared Axios Instance ──────────────────────────────────────
const api = axios.create({
  baseURL: DEMO_CONFIG.BACKEND_URL,
  headers: {
    'Content-Type': 'application/json',
    'X-API-Key': DEMO_CONFIG.API_KEY,
  },
  withCredentials: true, // send cookies too (session token)
});


// ─── 0. DoqSolv: Fetch Template Schema ──────────────────────────
/**
 * Fetches the template's variable definitions so we can render
 * a dynamic form that matches the template exactly.
 *
 * GET /api/external/v1/doqsolv/templates/{templateId}
 * @returns {{ template: { id, name, variables: VariableDefinition[], ... } }}
 */
export async function fetchTemplateSchema(templateId) {
  const response = await api.get(`/api/external/v1/doqsolv/templates/${templateId}`);
  return response.data; // { success, template: { variables, name, ... } }
}


// ─── 1. DoqSolv: Generate Loan Agreement ────────────────────────
/**
 * Calls POST /api/external/v1/doqsolv/generate
 *
 * @param {Object} variables — key/value pairs matching the template schema
 * @returns {{ document_id, template_name, pdf_file_id }}
 */
export async function generateLoanAgreement(variables) {
  const response = await api.post('/api/external/v1/doqsolv/generate', {
    template_id: DEMO_CONFIG.TEMPLATE_ID,
    variables,
  });

  return response.data;
}


// ─── 1b. DoqSolv: Download Generated PDF ────────────────────────
/**
 * Calls GET /api/external/v1/doqsolv/documents/{id}/download
 * Returns raw PDF bytes as an ArrayBuffer.
 */
export async function downloadGeneratedPdf(documentId) {
  const response = await api.get(
    `/api/external/v1/doqsolv/documents/${documentId}/download`,
    { responseType: 'arraybuffer' }
  );
  return response.data; // ArrayBuffer
}


// ─── 2. DocuStamp: Consume Stamp Paper ──────────────────────────
/**
 * Calls POST /api/external/v2/documents/consume
 * Pulls the next available stamp paper from the configured bulk order.
 *
 * @returns {{ status, document_id, mime_type, file_size, file_base64, metadata }}
 */
export async function consumeStampPaper() {
  const requestId = `demo-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

  const response = await api.post('/api/external/v2/documents/consume', {
    order_id: DEMO_CONFIG.BULK_ORDER_ID,
    request_id: requestId,
  });

  return response.data;
}


// ─── 3. Client-Side PDF Merge ───────────────────────────────────
/**
 * Merges a stamp paper PDF (page 1) with the agreement PDF (pages 2+)
 * using pdf-lib. Everything happens in the browser — no server round-trip.
 *
 * @param {ArrayBuffer|Uint8Array} stampPdfBytes  - Stamp paper PDF bytes
 * @param {ArrayBuffer|Uint8Array} agreementPdfBytes - Agreement PDF bytes
 * @returns {Uint8Array} Merged PDF bytes
 */
export async function mergePdfs(stampPdfBytes, agreementPdfBytes) {
  const mergedPdf = await PDFDocument.create();

  // Load both source PDFs
  const stampDoc = await PDFDocument.load(stampPdfBytes);
  const agreementDoc = await PDFDocument.load(agreementPdfBytes);

  // Copy stamp paper pages first
  const stampPages = await mergedPdf.copyPages(stampDoc, stampDoc.getPageIndices());
  stampPages.forEach(page => mergedPdf.addPage(page));

  // Then append agreement pages
  const agreementPages = await mergedPdf.copyPages(agreementDoc, agreementDoc.getPageIndices());
  agreementPages.forEach(page => mergedPdf.addPage(page));

  return await mergedPdf.save(); // Uint8Array
}


// ─── 4. eSign: Direct Send (with merged PDF base64) ────────────
/**
 * Uses the direct eSign send endpoint with the merged PDF.
 * POST /api/external/esign/send
 *
 * @param {Uint8Array} mergedPdfBytes - The merged PDF bytes
 * @param {string} signerName - Full name of the signer
 * @param {string} signerEmail - Email for the signer
 * @param {string} signerMobile - Mobile number of the signer
 * @returns {{ success, envelope_id, esign_url, status, message }}
 */
export async function sendMergedForEsign(mergedPdfBytes, signerName, signerEmail, signerMobile) {
  // Convert to base64
  const base64 = uint8ArrayToBase64(mergedPdfBytes);

  const response = await api.post('/api/external/esign/send', {
    signer_name: signerName,
    signer_email: signerEmail,
    signer_mobile: signerMobile,
    document_name: `Loan_Agreement_${signerName.replace(/\s+/g, '_')}.pdf`,
    document_data: base64,
    reason: 'Loan Agreement Signing',
    days_to_expire: 7,
    esign_type: 'EKYC',
    signature_coordinates: [
      { page_number: 1, x_coordinate: 130, y_coordinate: 100, width: 103, height: 50 },
    ],
    redirect_url: `${window.location.origin}/loan-demo?esign_status=success`,
    cancel_redirect_url: `${window.location.origin}/loan-demo?esign_status=cancelled`,
  });

  return response.data;
}


// ─── 5. Welcome Email: Send Branded Onboarding Email ────────────
/**
 * Sends a branded "Welcome to NeoBank" HTML email with the generated
 * document attached as a PDF. Uses the new welcome-email endpoint
 * which has an inline HTML template.
 *
 * POST /api/external/v1/doqsolv/integrations/welcome-email
 *
 * @param {string} recipientEmail - Email to send to
 * @param {string} recipientName - Full name of the recipient
 * @param {string} documentId - DoqSolv document ID (attached as PDF)
 * @param {string} [companyName='NeoBank'] - Brand name for the email
 * @returns {{ success, message, simulated }}
 */
export async function sendWelcomeEmail(recipientEmail, recipientName, documentId, companyName = 'NeoBank') {
  const response = await api.post('/api/external/v1/doqsolv/integrations/welcome-email', {
    recipient_email: recipientEmail,
    recipient_name: recipientName,
    company_name: companyName,
    document_id: documentId || undefined,
  });

  return response.data;
}


// ─── Helpers ────────────────────────────────────────────────────

/**
 * Converts a Uint8Array to a Base64 string.
 */
function uint8ArrayToBase64(bytes) {
  let binary = '';
  const chunk = 8192;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

/**
 * Decodes a Base64 string to a Uint8Array.
 */
export function base64ToUint8Array(base64) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}
